import React, { useState } from 'react'
import { Heart, Shield, CreditCard, Banknote, CheckCircle, Star, Users, BookOpen, Home as HomeIcon } from 'lucide-react'
import './Donate.css'

const amounts = [500, 1000, 2500, 5000, 10000]

const impactItems = [
  { icon: BookOpen, amount: '₹500', desc: 'Provides books & stationery for one child for a full year.' },
  { icon: HomeIcon, amount: '₹1,000', desc: 'Covers one month of hostel stay including meals for a child.' },
  { icon: Users, amount: '₹2,500', desc: 'Sponsors a month of skill training for one youth.' },
  { icon: Heart, amount: '₹5,000', desc: 'Covers health check-ups & hygiene kits for 5 children.' },
]

export default function Donate() {
  const [selected, setSelected] = useState(1000)
  const [custom, setCustom] = useState('')
  const [mode, setMode] = useState('online')
  const [submitted, setSubmitted] = useState(false)
  const [form, setForm] = useState({ name: '', email: '', pan: '', message: '' })

  const finalAmount = custom ? parseInt(custom) || 0 : selected

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!form.name || !form.email) return
    setSubmitted(true)
  }

  return (
    <div className="donate-page">
      <section className="donate-hero">
        <div className="container donate-hero__inner">
          <div>
            <span className="section-tag" style={{background:'rgba(255,255,255,0.2)', color:'white'}}>Make a Difference</span>
            <h1 className="donate-hero__title">Your Generosity Changes Lives</h1>
            <p className="donate-hero__desc">
              Every rupee you donate goes directly to supporting tribal children's education, food, shelter, and skill development — free of charge, for those who need it most.
            </p>
            <div className="donate-hero__trust">
              <Shield size={16}/> 100% transparent fund usage &nbsp;|&nbsp; <Star size={16}/> Registered NGO &nbsp;|&nbsp; <CheckCircle size={16}/> Tax exemption under 80G
            </div>
          </div>
          <div className="donate-hero__impact">
            <h3 className="donate-hero__impact-title">Your Donation's Impact</h3>
            {impactItems.map(item => (
              <div key={item.amount} className="impact-item">
                <div className="impact-item__icon"><item.icon size={18}/></div>
                <div>
                  <span className="impact-item__amount">{item.amount}</span>
                  <span className="impact-item__desc"> — {item.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="page-section">
        <div className="container donate-form-wrap">
          {submitted ? (
            <div className="donate-success card">
              <div className="donate-success__icon"><Heart size={36} fill="#0ea5e9" color="#0ea5e9"/></div>
              <h2>Thank You, {form.name}!</h2>
              <p>Your generous donation of <strong>₹{finalAmount.toLocaleString()}</strong> is being processed. You will receive a confirmation and 80G receipt at <strong>{form.email}</strong> shortly.</p>
              <p className="donate-success__sub">Every rupee will directly support our tribal children. From all of us at Malyabanta Swechhasevi Samiti — <em>sincere gratitude.</em></p>
              <button className="btn btn-primary" onClick={() => { setSubmitted(false); setForm({ name:'',email:'',pan:'',message:'' }) }}>Make Another Donation</button>
            </div>
          ) : (
            <div className="donate-form-grid">
              {/* Amount selector */}
              <div className="card donate-card">
                <h2 className="donate-card__title">Choose Amount</h2>
                <div className="amount-grid">
                  {amounts.map(a => (
                    <button
                      key={a}
                      className={`amount-btn${selected === a && !custom ? ' active' : ''}`}
                      onClick={() => { setSelected(a); setCustom('') }}
                    >₹{a.toLocaleString()}</button>
                  ))}
                </div>
                <div className="amount-custom">
                  <label className="form-label">Or enter custom amount (₹)</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g. 3500"
                    value={custom}
                    onChange={e => { setCustom(e.target.value); setSelected(0) }}
                    min="1"
                  />
                </div>

                <div className="donate-mode">
                  <h3 className="donate-mode__title">Payment Method</h3>
                  <div className="donate-mode__btns">
                    <button className={`mode-btn${mode==='online'?' active':''}`} onClick={() => setMode('online')}>
                      <CreditCard size={18}/> Online / UPI
                    </button>
                    <button className={`mode-btn${mode==='bank'?' active':''}`} onClick={() => setMode('bank')}>
                      <Banknote size={18}/> Bank Transfer
                    </button>
                  </div>
                  {mode === 'bank' && (
                    <div className="bank-details">
                      <p><strong>Account Name:</strong> Malyabanta Swechhasevi Samiti</p>
                      <p><strong>Account No:</strong> 35756295450</p>
                      <p><strong>IFSC:</strong> SBIN0004853</p>
                      <p><strong>Bank:</strong> State Bank of India, Orkel Branch</p>
                      <p><strong>UPI ID:</strong> mssyettocome@sbi</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Donor details */}
              <form className="card donate-card" onSubmit={handleSubmit}>
                <h2 className="donate-card__title">Your Details</h2>
                <div className="form-field">
                  <label className="form-label">Full Name <span className="req">*</span></label>
                  <input className="form-input" type="text" placeholder="Your full name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required/>
                </div>
                <div className="form-field">
                  <label className="form-label">Email Address <span className="req">*</span></label>
                  <input className="form-input" type="email" placeholder="you@example.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required/>
                </div>
                <div className="form-field">
                  <label className="form-label">PAN Number <span className="optional">(for 80G receipt)</span></label>
                  <input className="form-input" type="text" placeholder="ABCDE1234F" value={form.pan} onChange={e => setForm({...form, pan: e.target.value})} maxLength={10} style={{textTransform:'uppercase'}}/>
                </div>
                <div className="form-field">
                  <label className="form-label">Message <span className="optional">(optional)</span></label>
                  <textarea className="form-input form-textarea" placeholder="Any message for us..." value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={3}/>
                </div>
                <div className="donate-summary">
                  Donating <strong>₹{finalAmount > 0 ? finalAmount.toLocaleString() : '—'}</strong> via {mode === 'online' ? 'Online / UPI' : 'Bank Transfer'}
                </div>
                <button type="submit" className="btn btn-primary donate-submit" disabled={!finalAmount || finalAmount < 1}>
                  <Heart size={18} fill="white"/> Donate ₹{finalAmount > 0 ? finalAmount.toLocaleString() : '—'}
                </button>
                <p className="donate-note"><Shield size={13}/> Your information is secure and never shared.</p>
              </form>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
